import {
  keepPreviousData,
  useMutation,
  useQuery,
} from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import useQSBuilder from "../../hooks/useQSBuilder";
import { IDefaultResponse } from "../../models/DefaultResponse";
import {
  ITicketInsightAnalyseRequest,
  ITicketInsightAnalyseResult,
  ITicketInsightAnalytics,
  ITicketInsightQuery,
  ITicketInsightStatus,
} from "../../models/Tickets/TicketInsight";
import { TicketQueryKeys } from "./keys";

function buildParams(query: ITicketInsightQuery) {
  const params: Record<string, string> = {
    days: String(query.days),
    includeResolved: String(query.includeResolved),
  };
  if (query.customerId) params.customerId = query.customerId;
  if (query.similarityThreshold)
    params.similarityThreshold = String(query.similarityThreshold);
  return params;
}

/**
 * The AI review of the queue. Analytics are computed server-side from stored
 * insights, so this is cheap to refetch; running the model is a separate
 * mutation (`analyse`) that the page loops until nothing is pending.
 */
export default function useTicketInsights(query: ITicketInsightQuery) {
  const { get, post } = useApiHelper();
  const buildQS = useQSBuilder();

  const queryString = buildQS(buildParams(query));

  const analytics = useQuery({
    queryKey: [...TicketQueryKeys.insights(), queryString],
    queryFn: () =>
      get<IDefaultResponse<ITicketInsightAnalytics>>(
        `/ticket/insights${queryString}`,
      ).then((res) => res.data),
    placeholderData: keepPreviousData,
  });

  const status = useQuery({
    queryKey: [...TicketQueryKeys.insightStatus(), queryString],
    queryFn: () =>
      get<IDefaultResponse<ITicketInsightStatus>>(
        `/ticket/insights/status${queryString}`,
      ).then((res) => res.data),
    placeholderData: keepPreviousData,
  });

  // Deliberately no onSuccess invalidation: a run is many batches, and refetching the
  // whole analytics payload after each one would hammer the API for no visible gain.
  // The page refetches once the loop finishes.
  const analyse = useMutation({
    mutationFn: (request: ITicketInsightAnalyseRequest) =>
      post<
        IDefaultResponse<ITicketInsightAnalyseResult>,
        ITicketInsightAnalyseRequest
      >(`/ticket/insights/analyse`, request).then((res) => res.data),
  });

  return {
    analytics,
    status,
    analyse,
  };
}
